import { SelectOption } from '@globaleris/ui.library'
import { CicloFacturacion } from '../../api'
import {
  apiDeleteFlexibility,
  apiPatchFlexibility,
  apiPostFlexibility,
  apifetchCalculationConceptTypes,
  apifetchCalculationConceptTypesForProduct,
  apifetchFlexibilityGroups,
  apifetchFlexibilitySubtypeLimits,
  apifetchFlexibilitySubtypesByCalculationTypes,
  apifetchFlexibilitySubtypesQuantityTypes,
  fetchFlexibilityByID,
  fetchLimit,
  patchFlexibilityLimit,
  updateFlexibilityLimits
} from '../../api/Argentia/endpoints/flexibilities/flexibility.api'
import {
  CalculationConceptType,
  FlexibilityModel,
  FlexibilitySubtypeLimits,
  FlexibilitySubtypeQuantityTypes,
  NewFlexibilityRequest,
  PatchFlexibilityRequest
} from '../../api/Argentia/endpoints/flexibilities/model/flexibilities'
import { ApiTrades } from '../../api/Pioneer/endpoints/trades/trades.api'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'
import {
  consolidateQuantityFlexibilityResponse,
  flexibilityGroupToSelectOptionMapper,
  flexibilitySubtypeToSelectOptionMapper,
  patchFlexibilityLimitByIDRequestPatch
} from './flexibility.mapper'
import { FlexibilityDetails, Limit, QuantityFlexibility } from './flexibility.Model'
import { enumKeyToValue } from '../../helpers'
import { MeasureUnitType } from '../../api/Pioneer/enums/measureUnitType'
import { MeasureUnit } from '../../api/Pioneer/model/MeasureUnit'
import { apiFetchContractQuantitiesForContract } from '../../api/Argentia/endpoints/contracts/contracts.api'
import { EditablePriceModel } from '../../api/Argentia/model/editablePriceModel'
import { apiPatchPrice } from '../../api/Argentia/endpoints/pricing/pricing.api'
import { PriceDetails } from '../pricing/pricing.Model'
import moment from 'moment'
import { apiFetchInvoiceCycles } from '../../api/Pioneer/endpoints/invoiceCycles/invoiceCycles.api'
import { invoiceCyclesMapperToDropDown } from '../trades/trades.mapper'
import { apiPatchPriceVersion } from '../../api/Argentia/endpoints/versions/versions.api'
import { EditablePriceVersionModel } from '../../api/Argentia/endpoints/versions/model/EditablePriceVersion.model'
//import DataMock from '../flexibility/flexibility.mock.json'

const apiTrades = new ApiTrades()

export const fetchFlexibilityDetails = async (
  id: number
): Promise<ServiceResponse<FlexibilityModel | undefined>> => {
  return await fetchFlexibilityByID(id)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchFlexibilityLimits = async (
  id: number
): Promise<ServiceResponse<QuantityFlexibility | undefined>> => {
  return await fetchLimit(id)
    .then(res => {
      return createSuccessServiceResponse(consolidateQuantityFlexibilityResponse(res))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const postFlexibility = async (
  request: NewFlexibilityRequest
): Promise<ServiceResponse<any>> => {
  return await apiPostFlexibility(request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const patchFlexibilityLimitByID = async (
  flexibilityId: number,
  limitId: number,
  percentage: number
): Promise<ServiceResponse<any>> => {
  const patchRequest = patchFlexibilityLimitByIDRequestPatch(percentage)
  return await patchFlexibilityLimit(flexibilityId, limitId, patchRequest)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateFlexibilityQuantity = async (
  flexibilityId: number,
  request: QuantityFlexibility
): Promise<any> => {
  return await updateFlexibilityLimits(flexibilityId, request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

// Dependencies

export const fetchCalculationConceptTypes = async () => {
  return await apifetchCalculationConceptTypes()
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchCalculationConceptTypesForProduct = async (
  productID: number
): Promise<ServiceResponse<CalculationConceptType[] | undefined>> => {
  return await apifetchCalculationConceptTypesForProduct(productID)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchFlexibilitySubtypesForCalculationTypes = async (
  calculationConceptType: number[]
): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  return await apifetchFlexibilitySubtypesByCalculationTypes(calculationConceptType)
    .then(res => {
      return createSuccessServiceResponse(flexibilitySubtypeToSelectOptionMapper(res))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchFlexibilityGroups = async (): Promise<
  ServiceResponse<SelectOption[] | undefined>
> => {
  return await apifetchFlexibilityGroups()
    .then(res => {
      return createSuccessServiceResponse(flexibilityGroupToSelectOptionMapper(res))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchInvoiceCycles = async (): Promise<
  ServiceResponse<SelectOption[] | undefined>
> => {
  return await apiFetchInvoiceCycles(3)
    .then(res => {
      return createSuccessServiceResponse(invoiceCyclesMapperToDropDown(res))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchFlexibilitySubtypesLimits = async (): Promise<
  ServiceResponse<FlexibilitySubtypeLimits[] | undefined>
> => {
  return await apifetchFlexibilitySubtypeLimits()
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchFlexibilitySubtypesQuantityTypes = async (): Promise<
  ServiceResponse<FlexibilitySubtypeQuantityTypes[] | undefined>
> => {
  return await apifetchFlexibilitySubtypesQuantityTypes()
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchMeasureUnitPowerType = async (): Promise<
  ServiceResponse<MeasureUnit[] | undefined>
> => {
  return await apiTrades
    .FetchMeasureUnits()
    .then(res => {
      const options = res.filter(
        mu => mu.unitType === enumKeyToValue(MeasureUnitType, MeasureUnitType.Potencia)
      )
      return createSuccessServiceResponse(options)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchContractQuantityLimits = async (
  contractID: number
): Promise<ServiceResponse<Limit | undefined>> => {
  return await apiFetchContractQuantitiesForContract(contractID)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const patchPrice = async (price: PriceDetails): Promise<ServiceResponse<any>> => {
  const priceVersion: EditablePriceVersionModel = {
    expression: price.priceExpression,
    precioUnitario: price.precioUnitario,
    priceCalculationType: price.priceCalculationType,
    redondeo: price.redondeo
  }
  return await apiPatchPriceVersion(price.id, priceVersion)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const patchFlexibility = async (
  flexibility: FlexibilityDetails
): Promise<ServiceResponse<any>> => {
  const request: PatchFlexibilityRequest = {
    description: flexibility.description,
    startDate: moment(flexibility.startDate).format('YYYY-MM-DD'),
    endDate: moment(flexibility.endDate).format('YYYY-MM-DD'),
    signDate: flexibility.signDate ? moment(flexibility.signDate).format('YYYY-MM-DD') : null,
    calculationPeriod: flexibility.calculationPeriod,
    invoiceCycleId: flexibility.invoicingCycleId ?? 0,
    periodType: flexibility.periodType,
    settlementPeriod: flexibility.settlementPeriod,
    flexibilityGroup: flexibility.flexibilityGroup ?? '',
    comments: flexibility.comments
  }

  return await apiPatchFlexibility(flexibility.id, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
